/**
 * Aspect — Device profile (desktop vs phone)
 * --------------------------------------------------------------
 * Desktop rendering stays on the existing high path. Phones (iOS Safari
 * and Android Chrome / Samsung / Firefox) share one tour profile: bedroom
 * only, compact HUD, touch walk/look, USD prices.
 *
 * Rapid crash-reload loops (mostly Safari killing the tab) tighten the GPU
 * budget further.
 */

export function isAppleMobile() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  // iPadOS 13+ reports as Macintosh with a touch screen.
  return navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1;
}

export function isAndroid() {
  if (typeof navigator === "undefined") return false;
  return /Android/i.test(navigator.userAgent || "");
}

/**
 * Phone / tablet tour (not a Windows laptop with a touch screen).
 * Android UAs are included even when Chrome reports pointer:fine (desktop site,
 * DeX, some WebViews).
 */
export function isTouchUi() {
  if (typeof document !== "undefined" && document.body?.classList.contains("touch-ui")) return true;
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  if (isAndroid() || isAppleMobile()) return true;
  return Boolean(window.matchMedia?.("(pointer: coarse)")?.matches);
}

export function isEmbeddedIframe() {
  try {
    return typeof window !== "undefined" && window.self !== window.top;
  } catch {
    return true;
  }
}

const BOOT_AT = "aspect_safari_boot_at";
const BOOT_LOOPS = "aspect_safari_loops";

/** Count rapid reloads (Safari killing the tab and auto-refreshing). */
export function noteSafariBoot() {
  if (typeof sessionStorage === "undefined") return 0;
  const now = Date.now();
  const last = Number(sessionStorage.getItem(BOOT_AT) || 0);
  const loops = Number(sessionStorage.getItem(BOOT_LOOPS) || 0);
  const next = now - last < 30000 ? loops + 1 : 0;
  sessionStorage.setItem(BOOT_AT, String(now));
  sessionStorage.setItem(BOOT_LOOPS, String(next));
  return next;
}

export function safariReloadLoops() {
  if (typeof sessionStorage === "undefined") return 0;
  return Number(sessionStorage.getItem(BOOT_LOOPS) || 0);
}

/** Hard cap on live GLB instances. Unused ones are destroyed, not pooled. */
export function safariMaxLiveFurniture() {
  const loops = safariReloadLoops();
  if (loops >= 2) return 6;
  if (loops >= 1) return 8;
  return 10;
}

/**
 * Phone graphics preset. Bedroom-only is small enough for shadows + HDR.
 * Rapid Safari crash-reloads step back to a safer preset.
 * @returns {"desktop"|"hq"|"medium"|"safe"}
 */
export function mobileGraphicsTier() {
  if (!isTouchUi()) return "desktop";
  const loops = safariReloadLoops();
  if (loops >= 2) return "safe";
  if (loops >= 1) return "medium";
  return "hq";
}

/**
 * Sample Tour on aspect.ge must keep https://aspect.ge/SampleTour in the
 * address bar. Do not navigate the parent tab to fly.dev.
 * @returns {boolean} true if navigation was requested
 */
export function breakOutOfIframeOnMobile() {
  return false;
}
